import { useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import type {
    MenuForcedState,
    MenuRootProps,
} from "@design-system/Menu/Menu.lib";
import { CopyIcon, EditIcon, LinkIcon, TrashIcon } from "@icons";
import {
    buildTriggerButton,
    moreTriggerProps,
    type MenuPlaygroundTriggerOptions,
} from "./MenuPlaygroundTrigger";

/** What the stage's first row is; each kind is one anatomy variant. */
export type MenuPlaygroundRowKind = "item" | "submenu" | "toggle" | "radio";

interface StageProps {
    rowKind: MenuPlaygroundRowKind;
    isDestructive: boolean;
    isDisabled: boolean;
    forcedState: MenuForcedState | undefined;
    onAction: (action: string) => void;
    trigger: MenuPlaygroundTriggerOptions;
    isOpen: boolean;
    onOpenChange: NonNullable<MenuRootProps["onOpenChange"]>;
}

const disabledReason = "Locked while the period is closed";

type Density = "compact" | "comfortable";

interface FirstRowProps extends StageProps {
    isShowingVoided: boolean;
    onShowVoidedChange: (isShowingVoided: boolean) => void;
    density: Density;
    onDensityChange: (density: Density) => void;
}

const isDensity = (value: string): value is Density =>
    value === "compact" || value === "comfortable";

/** The first row in the aside's kind, disabled and forced state. */
const FirstRow = ({
    rowKind,
    isDestructive,
    isDisabled,
    forcedState,
    onAction,
    isShowingVoided,
    onShowVoidedChange,
    density,
    onDensityChange,
}: FirstRowProps) => {
    const rowState = {
        disabled: isDisabled,
        disabledReason,
        "data-forced-state": forcedState,
    };

    switch (rowKind) {
        case "submenu":
            return (
                <Menu.Submenu
                    {...rowState}
                    leading={<EditIcon />}
                    label="Move to"
                >
                    <Menu.Item onClick={() => onAction("move:draft")}>
                        Draft
                    </Menu.Item>
                    <Menu.Item onClick={() => onAction("move:sent")}>
                        Sent
                    </Menu.Item>
                    <Menu.Item onClick={() => onAction("move:archived")}>
                        Archived
                    </Menu.Item>
                </Menu.Submenu>
            );
        case "toggle":
            return (
                <Menu.ToggleItem
                    {...rowState}
                    checked={isShowingVoided}
                    onCheckedChange={(isChecked) => {
                        onShowVoidedChange(isChecked);
                        onAction(`show voided: ${isChecked}`);
                    }}
                >
                    Show voided lines
                </Menu.ToggleItem>
            );
        case "radio":
            return (
                <Menu.RadioGroup
                    value={density}
                    onValueChange={(value) => {
                        if (isDensity(value)) {
                            onDensityChange(value);
                        }
                        onAction(`density: ${value}`);
                    }}
                >
                    {/* The aside's state lands on the selected row, the one the x-ray draws. */}
                    <Menu.RadioItem
                        {...(density === "compact" ? rowState : {})}
                        value="compact"
                    >
                        Compact
                    </Menu.RadioItem>
                    <Menu.RadioItem
                        {...(density === "comfortable" ? rowState : {})}
                        value="comfortable"
                    >
                        Comfortable
                    </Menu.RadioItem>
                </Menu.RadioGroup>
            );
        case "item":
            return (
                <Menu.Item
                    {...rowState}
                    leading={<EditIcon />}
                    destructive={isDestructive}
                    onClick={() => onAction("edit")}
                >
                    Edit
                </Menu.Item>
            );
    }
};

/**
 * The live menu: its trigger follows the aside's Trigger section and its
 * first row the row kind, toggles and state. Toggle and radio values live
 * here, outside the popup, which unmounts on close.
 */
export const MenuPlaygroundStage = (props: StageProps) => {
    const [isShowingVoided, setIsShowingVoided] = useState(true);
    const [density, setDensity] = useState<Density>("compact");
    const rows = (
        <>
            <Menu.Group>
                <Menu.GroupLabel>Invoice</Menu.GroupLabel>
                <FirstRow
                    {...props}
                    isShowingVoided={isShowingVoided}
                    onShowVoidedChange={setIsShowingVoided}
                    density={density}
                    onDensityChange={setDensity}
                />
                <Menu.Item
                    leading={<CopyIcon />}
                    rightText="3 lines"
                    onClick={() => props.onAction("duplicate")}
                >
                    Duplicate
                </Menu.Item>
                <Menu.LinkItem
                    leading={<LinkIcon />}
                    to="/design-system/picker"
                >
                    Open the Picker playground
                </Menu.LinkItem>
            </Menu.Group>
            <Menu.Separator />
            <Menu.Item
                leading={<TrashIcon />}
                destructive
                onClick={() => props.onAction("delete")}
            >
                Delete
            </Menu.Item>
        </>
    );

    return props.trigger.kind === "more" ? (
        <Menu.More
            {...moreTriggerProps(props.trigger)}
            aria-label="Invoice actions"
            open={props.isOpen}
            onOpenChange={props.onOpenChange}
        >
            {rows}
        </Menu.More>
    ) : (
        <Menu.Root
            trigger={buildTriggerButton(props.trigger)}
            open={props.isOpen}
            onOpenChange={props.onOpenChange}
        >
            {rows}
        </Menu.Root>
    );
};
